// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { Moves } from '@gql/base-types';

import { css } from '@emotion/css';
import { useTheme, Theme } from '@mui/material';

import { Divider } from '@hopdrive/sdk-ui';

import MovesDashboardTile from '@features/moves/components/MovesDashboardTile';

// HELPERS ---------------------------------------------------------------- //

const acceptedOrderByValues = [`consumer_name`, `driver_name`, `createdBy`];

const getFallbackString = (orderBy: string) => {
  if (orderBy === 'consumer_name') return 'No Customer';
  if (orderBy === 'driver_name') return 'No Driver';
  if (orderBy === 'createdBy') return 'Unknown';
  return 'N/A';
};

const groupMoves = (moves: Moves[], orderBy: string) => {
  // Return empty if orderBy is not in the accepted values
  if (!acceptedOrderByValues.includes(orderBy)) {
    return [];
  }

  // Get the fallback string
  const fallbackString = getFallbackString(orderBy);

  // Group moves by the orderBy value (non case-sensitive)
  // If there is no value, group by unknown
  const groups: Record<string, Moves[]> = {} as Record<string, Moves[]>;
  moves.forEach(move => {
    let value = move?.[orderBy as keyof Moves];

    // If the value contains "API-", set it to "API"
    if (typeof value === 'string' && value.includes('API-')) {
      value = 'API';
    }

    if (value) {
      if (!groups[value as string]) {
        groups[value as string] = [];
      }
      groups[value as string].push(move);
    } else {
      if (!groups[fallbackString]) {
        groups[fallbackString] = [];
      }
      groups[fallbackString].push(move);
    }
  });
  return groups;
};

// TYPES ---------------------------------------------------------------- //

interface SearchResultsGridProps {
  moves: Moves[];
  orderBy: string;
  listOfAssigning?: any[];
  handleAssignOrUnassignModalOpen?: (input: any) => void;
}

// COMPONENT ---------------------------------------------------------------- //

const SearchResultsGrid = ({
  moves,
  orderBy,
  listOfAssigning,
  handleAssignOrUnassignModalOpen,
}: SearchResultsGridProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const moveGroups = groupMoves(moves, orderBy);

  if (Object.keys(moveGroups).length > 0) {
    return Object.entries(moveGroups).map(([value, moves]) => (
      <div key={value} className={cls.container}>
        <Divider mb={6}>{value || getFallbackString(orderBy)}</Divider>

        <div className={cls.grid}>
          {moves.map(move => (
            <div key={move.id} className={cls.gridItem}>
              <MovesDashboardTile
                move={move}
                isAssigning={listOfAssigning?.map(assigning => assigning?.moveId).includes(move?.id)}
                handleAssignOrUnassignModalOpen={handleAssignOrUnassignModalOpen}
              />
            </div>
          ))}
        </div>
      </div>
    ));
  }

  return (
    <div className={cls.container}>
      <div className={cls.grid}>
        {moves.map(move => (
          <div key={move.id} className={cls.gridItem}>
            <MovesDashboardTile
              move={move}
              isAssigning={listOfAssigning?.map(assigning => assigning?.moveId).includes(move?.id)}
              handleAssignOrUnassignModalOpen={handleAssignOrUnassignModalOpen}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  container: css`
    padding: 24px;
    background-color: ${theme.palette.background.paper};
  `,
  grid: css`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: flex-start;
  `,
  gridItem: css`
    flex: 1 1 480px;
    max-width: 720px;
    min-width: 480px;
  `,
});

// EXPORT ---------------------------------------------------------------- //

// Memoize the component to prevent unnecessary re-renders
// Only re-render when moves data or assignment state changes
export default React.memo(SearchResultsGrid, (prevProps, nextProps) => {
  // Check if moves array has actually changed
  const movesEqual =
    prevProps.moves.length === nextProps.moves.length &&
    prevProps.moves.every((move, index) => move.id === nextProps.moves[index]?.id);

  // Check if assignment list has changed
  const assigningEqual = JSON.stringify(prevProps.listOfAssigning) === JSON.stringify(nextProps.listOfAssigning);

  // Return true if props are equal (skip re-render), false if different (re-render)
  return movesEqual && assigningEqual;
});
